(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class AbokImageProgress extends HTMLElement {
    connectedCallback() {
      this.teardown();
      this.items = [...this.querySelectorAll('[data-abok-image-progress-item]')];
      this.animationFrames = new Map();
      this.currentValues = new Map();
      this.abortController = new AbortController();
      this.handleReducedMotion = this.handleReducedMotion.bind(this);
      reducedMotion.addEventListener('change', this.handleReducedMotion, {
        signal: this.abortController.signal,
      });

      if (!this.items.length) return;

      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        this.completeAll();
        return;
      }

      this.items.forEach((item) => this.render(item, 0));
      const trigger = this.querySelector('[data-abok-image-progress-list]') || this;
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.intersectionObserver.disconnect();
          this.items.forEach((item) => this.animate(item));
        },
        { threshold: 0.35 },
      );
      this.intersectionObserver.observe(trigger);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.items.forEach((item) => this.render(item, this.currentValues.get(item) ?? 0));
        });
        this.items.forEach((item) => {
          const track = item.querySelector('[data-abok-progress-track]');
          if (track) this.resizeObserver.observe(track);
        });
      }
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.animationFrames?.forEach((rafId) => cancelAnimationFrame(rafId));
      this.animationFrames?.clear();
      this.abortController?.abort();
    }

    handleReducedMotion() {
      if (!reducedMotion.matches) return;
      this.intersectionObserver?.disconnect();
      this.animationFrames.forEach((rafId) => cancelAnimationFrame(rafId));
      this.animationFrames.clear();
      this.completeAll();
    }

    completeAll() {
      this.items.forEach((item) => {
        item.classList.remove('is-animating');
        this.render(item, this.targetFor(item));
      });
    }

    animate(item) {
      const target = this.targetFor(item);
      const configuredDuration = Number.parseInt(item.dataset.duration || '', 10);
      const duration = Number.isFinite(configuredDuration) && configuredDuration > 0
        ? clamp(configuredDuration, 100, 10000)
        : 2000;

      if (target <= 0) {
        this.render(item, 0);
        return;
      }

      item.classList.add('is-animating');
      const startedAt = performance.now();
      const tick = (now) => {
        this.animationFrames.delete(item);
        const elapsed = clamp((now - startedAt) / duration, 0, 1);
        const eased = 1 - Math.pow(1 - elapsed, 4);
        this.render(item, target * eased);

        if (elapsed < 1) {
          this.animationFrames.set(item, requestAnimationFrame(tick));
        } else {
          item.classList.remove('is-animating');
          this.render(item, target);
        }
      };

      this.animationFrames.set(item, requestAnimationFrame(tick));
    }

    targetFor(item) {
      const target = Number.parseFloat(item.dataset.target || '0');
      return Number.isFinite(target) ? clamp(target, 0, 100) : 0;
    }

    render(item, value) {
      const current = clamp(value, 0, 100);
      const track = item.querySelector('[data-abok-progress-track]');
      const fill = item.querySelector('[data-abok-progress-fill]');
      const output = item.querySelector('[data-abok-progress-output]');
      if (!track || !fill || !output) return;

      const rounded = Math.round(current);
      this.currentValues.set(item, current);
      fill.style.setProperty('--abok-progress-scale', (current / 100).toFixed(4));
      output.textContent = `${rounded}%`;
      track.setAttribute('aria-valuenow', String(rounded));

      const trackWidth = track.clientWidth;
      const outputWidth = output.offsetWidth;
      const maximumX = Math.max(8, trackWidth - outputWidth - 16);
      const targetX = current / 100 * trackWidth - outputWidth - 16;
      output.style.setProperty('--abok-progress-label-x', `${clamp(targetX, 8, maximumX)}px`);
    }
  }

  if (!customElements.get('abok-image-progress')) {
    customElements.define('abok-image-progress', AbokImageProgress);
  }
})();
